// convex/system/licensing/index.ts
//
// Registro CENTRAL de licencias (vive en tu nube). Es la fuente de verdad para
// emitir, revocar y validar (heartbeat). El token firmado que recibe el cliente
// on-prem se deriva de este registro. Tabla global (no scoped por empresa),
// gestionada por super_admin — mismo patrón que `subscription_plans`.

import { defineTable } from "convex/server";
import { v } from "convex/values";
import { getModuleById } from "../../lib/config";

export const licenseEdition = v.union(
  v.literal("saas"),
  v.literal("desktop"),
  v.literal("onprem"),
  v.literal("free_desktop"),
);

export const licenseType = v.union(
  v.literal("lifetime"),
  v.literal("subscription"),
);

export const licenseStatus = v.union(
  v.literal("active"),
  v.literal("suspended"),
  v.literal("revoked"),
  v.literal("expired"),
);

export const validatorLicense = v.object({
  /** id público estable, ej. "lic_ab12cd34" (va dentro del token firmado) */
  licenseId: v.string(),

  // Cliente
  customerName: v.string(),
  customerEmail: v.optional(v.string()),
  /** vínculo opcional con una empresa (para SaaS/Desktop) */
  companyId: v.optional(v.id("companies")),

  // Alcance
  edition: licenseEdition,
  type: licenseType,
  /** ids de módulos permitidos (vacío = todos los de la edición) */
  modules: v.array(v.string()),
  /** límite de usuarios (0 = ilimitado) */
  seats: v.number(),
  /** límite de sucursales (0/ausente = ilimitado). Editable para el tier. */
  maxBranches: v.optional(v.number()),

  // Vigencia
  issuedAt: v.number(), // epoch ms
  /** epoch ms; ausente/null = vitalicia */
  expiresAt: v.optional(v.number()),
  /** días máximos sin heartbeat antes de degradar en el cliente */
  heartbeatGraceDays: v.number(),

  // Estado y telemetría de validación
  status: licenseStatus,
  lastHeartbeatAt: v.optional(v.number()),
  lastHeartbeatIp: v.optional(v.string()),
  /** veces que el cliente ha validado online */
  heartbeatCount: v.optional(v.number()),

  // One-shot: el primer `setupCompanyFromOnboarding` que use este licenseId
  // queda registrado. Un segundo uso con un email/empresa distinto se rechaza.
  consumed: v.optional(v.boolean()),
  activatedAt: v.optional(v.number()),
  activatedEmail: v.optional(v.string()),
  activatedCompanyId: v.optional(v.id("companies")),
  // Binding a instalación: fingerprint de la primera instalación que activa este
  // licenseId. Un heartbeat/activación con un fingerprint distinto se rechaza
  // (`activated_on_other_server`) → una licencia = una instalación. Ver
  // `docs/license-anti-piracy-design.md`.
  activatedFingerprint: v.optional(v.string()),

  // Token firmado (cacheado en la nube para que el admin lo pueda re-mostrar
  // sin re-firmar). Solo presente en licencias emitidas desde la UI de super.
  token: v.optional(v.string()),

  // Revocación
  revokedAt: v.optional(v.number()),
  revokedReason: v.optional(v.string()),

  // Notas internas (no van al token)
  notes: v.optional(v.string()),
});

export const licenses = defineTable(validatorLicense)
  .index("by_license_id", ["licenseId"])
  .index("by_status", ["status"])
  .index("by_company_id", ["companyId"])
  .index("by_edition", ["edition"]);

/**
 * Singleton por instalación: un fingerprint estable (UUID) generado en el primer
 * boot del on-prem y persistido. Identifica ESTA instalación ante el central
 * para el binding de licencia. NO se deriva de un secreto reproducible: se
 * genera y se guarda una sola vez. Solo debería existir una fila.
 */
export const validatorLicenseInstall = v.object({
  fingerprint: v.string(),
  createdAt: v.number(),
  /** Token de licencia activado, persistido a nivel INSTALACIÓN (no por
   * navegador). Permite que cualquier navegador/dispositivo entre sin re-activar:
   * el gate lee este token del backend en vez de depender solo de localStorage. */
  token: v.optional(v.string()),
  licenseActivatedAt: v.optional(v.number()),
});

export const licenseInstall = defineTable(validatorLicenseInstall);

export const licenseRequestStatus = v.union(
  v.literal("pending"),
  v.literal("approved"),
  v.literal("rejected"),
);

/** Solicitudes públicas de licencia (vía POST /license/request). El super
 * admin las ve en /super/licenses y las aprueba para emitir el token. */
export const validatorLicenseRequest = v.object({
  // Cliente (datos que completó el formulario público)
  customerName: v.string(),
  customerEmail: v.string(),
  customerPhone: v.optional(v.string()),
  customerRtn: v.optional(v.string()),
  companyName: v.optional(v.string()),

  // Alcance pedido
  edition: licenseEdition,
  type: licenseType,
  seats: v.number(),
  maxBranches: v.optional(v.number()),
  notes: v.optional(v.string()),

  // Workflow
  status: licenseRequestStatus,
  createdAt: v.number(),
  resolvedAt: v.optional(v.number()),
  resolvedBy: v.optional(v.string()),
  resolvedLicenseId: v.optional(v.string()),
  rejectionReason: v.optional(v.string()),

  // IP del solicitante (para rate-limit básico)
  requestIp: v.optional(v.string()),
});

export const licenseRequests = defineTable(validatorLicenseRequest)
  .index("by_status", ["status"])
  .index("by_email", ["customerEmail"])
  .index("by_created_at", ["createdAt"]);

/**
 * Normaliza una lista de ids para `license.modules`: descarta cualquier string
 * que no sea un module id reconocido (`getModuleById(id) === undefined`) y
 * deduplica el resultado preservando el orden de aparición.
 *
 * `license.modules` se persiste como module ids PUROS (no tablas). Esta es la
 * garantía que necesita el frontend: deshabilitar un module no puede ser
 * revertido por una dependencia transitiva (ej. notifications.readOnlyTables
 * reintroduciendo `reports`). Si `ids` viene vacío, devuelve [] (módulo
 * "todos" — vacío = todos los de la edición, convención ya existente).
 *
 * Idempotente: si la entrada ya es pura, devuelve una copia deduplicada.
 */
export function normalizeLicenseModules(ids: readonly string[]): string[] {
  if (!ids || ids.length === 0) return [];
  const seen = new Set<string>();
  const out: string[] = [];
  for (const id of ids) {
    if (getModuleById(id) === undefined) continue;
    if (seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}
